'use client';

import { useEffect, useState } from 'react';
import * as formStyles from './styles/formStyles';

interface PropertyDetailsFormProps {
  dictionary: any;
  propertyType: 'apartment' | 'house';
  municipality: string;
  cityName: string;
  onSubmit: (data: PropertyDetails) => void;
  disableSubmit?: boolean;
}

interface PropertyDetails {
  livingArea: number;
  bedrooms: number;
  bathrooms: number;
  plotArea?: number;
  facade?: number;
  transactionCount?: number;
}

interface MunicipalityStats {
  medianPrice: number;
  growthRate: number;
  propertyCount: number;
}

export default function PropertyDetailsForm({ 
  dictionary, 
  propertyType, 
  municipality,
  cityName,
  onSubmit,
  disableSubmit = false 
}: PropertyDetailsFormProps) {
  const [errors, setErrors] = useState<{
    livingArea?: string;
    bedrooms?: string;
    bathrooms?: string;
    plotArea?: string;
    facade?: string;
  }>({});
  const [stats, setStats] = useState<MunicipalityStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [visibleStage, setVisibleStage] = useState(0);
  // Stage 0: Living Area + Plot Area (if house) visible
  // Stage 1: Bedrooms visible
  // Stage 2: Bathrooms visible
  // Stage 3: Facade Radios (if house) OR Button (if apartment) visible
  // Stage 4: Button (if house) visible

  // State for controlled inputs
  const [livingArea, setLivingArea] = useState<number | ''>( '');
  const [bedrooms, setBedrooms] = useState<number | ''>( '');
  const [bathrooms, setBathrooms] = useState<number | ''>( '');
  const [plotArea, setPlotArea] = useState<number | ''>( '');
  const [facadeValue, setFacadeValue] = useState<number | null>(propertyType === 'house' ? 3 : null); // Default to 3 for house

  // Fetch stats when component mounts
  useEffect(() => {
    const fetchStats = async () => {
      setIsLoadingStats(true);
      try {
        const response = await fetch('/api/municipality-stats', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            municipality,
            propertyType: propertyType
          }),
        });

        if (response.ok) {
          const stats = await response.json();
          setStats(stats);
        }
      } catch (error) {
        console.error('Error fetching real estate statistics:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchStats();
  }, [propertyType, municipality]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const livingArea = formData.get('livingArea') as string;
    const bedrooms = formData.get('bedrooms') as string;
    const bathrooms = formData.get('bathrooms') as string;

    let plotArea = null;
    let facadeValueToSend = null;

    if (propertyType === 'house') {
      plotArea = formData.get('plotArea') as string;
      facadeValueToSend = facadeValue; // Get from state
    }

    const newErrors: typeof errors = {};

    if (!livingArea || isNaN(Number(livingArea)) || Number(livingArea) <= 0) {
      newErrors.livingArea = dictionary?.sellingForm?.livingAreaRequired || 'Please enter a valid living area';
    }
    if (bedrooms === '' || isNaN(Number(bedrooms)) || Number(bedrooms) < 0) {
      newErrors.bedrooms = dictionary?.sellingForm?.bedroomsRequired || 'Please enter a valid number of bedrooms';
    }
    if (bathrooms === '' || isNaN(Number(bathrooms)) || Number(bathrooms) < 0) {
      newErrors.bathrooms = dictionary?.sellingForm?.bathroomsRequired || 'Please enter a valid number of bathrooms';
    }

    if (propertyType === 'house') {
      if (!plotArea || isNaN(Number(plotArea)) || Number(plotArea) <= 0) {
        newErrors.plotArea = dictionary?.sellingForm?.plotAreaRequired || 'Please enter a valid plot area';
      }
      // Add validation for facadeValue if needed (e.g., ensure it's not null)
      if (facadeValue === null) {
        // Maybe set an error or rely on default?
        // For now, assume default (4) is okay or handled by radio selection
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const dataToSubmit: PropertyDetails = {
      livingArea: Number(livingArea),
      bedrooms: Number(bedrooms),
      bathrooms: Number(bathrooms),
      plotArea: plotArea ? Number(plotArea) : undefined,
      facade: facadeValueToSend === null ? undefined : facadeValueToSend,
      transactionCount: stats?.propertyCount
    };

    console.log('Submitting Property Details:', dataToSubmit);
    onSubmit(dataToSubmit);
  };

  // Use constant for input classes
  const inputClasses = formStyles.inputElementBaseClasses;

  return (
    <>
      <form onSubmit={handleSubmit} className="w-full">
        <div className={`${isLoadingStats ? 'h-[15rem] md:min-h-0' : ''}`}>
          {isLoadingStats ? (
            <div className="flex items-center gap-2 text-zinc-800">
              <div className="w-4 h-4 border-2 border-zinc-800 border-t-transparent rounded-full animate-spin"></div>
              <span>{dictionary?.sellingForm?.loadingStats || 'Loading market statistics...'}</span>
            </div>
          ) : stats && (
            <div className="space-y-4">
              <div className="space-y-2">
                {(() => {
                  const template = dictionary?.sellingForm?.statsSummarySentence;
                  // Use PLURAL keys now
                  const translatedPropertyType = (propertyType === 'house' 
                    ? (dictionary?.sellingForm?.propertyTypeHouses || 'houses') 
                    : (dictionary?.sellingForm?.propertyTypeApartments || 'apartments')
                  ).toLowerCase(); // Keep lowercase for sentence flow
                  // Format price with dots as thousands separators, ignoring locale
                  const formattedMedianPrice = stats.medianPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                  // Format percentage with comma as decimal separator, ignoring locale, and round to one decimal place
                  const formattedGrowthRate = (stats.growthRate * 100).toFixed(1).replace('.', ',');

                  let sentenceHtml = '';
                  if (template) {
                    sentenceHtml = template
                      .replace('[PROPERTY_TYPE]', translatedPropertyType)
                      .replace('[MUNICIPALITY]', cityName)
                      .replace('[GROWTH_RATE]', formattedGrowthRate)
                      .replace('[MEDIAN_PRICE]', formattedMedianPrice);
                  } else {
                    // Fallback sentence if dictionary key is missing
                    sentenceHtml = `Median price for <strong>${translatedPropertyType}</strong> in <strong>${cityName}</strong>: <strong>€${formattedMedianPrice}</strong>, 5-year growth: <strong>${formattedGrowthRate}%</strong>.`;
                  }

                  return (
                    <>
                      <p className="text-zinc-800" dangerouslySetInnerHTML={{ __html: sentenceHtml }} />
                      {/* Source Tag */}
                      <span className="inline-block mt-1 bg-white text-black text-xs font-medium px-2 py-1 rounded-2xl shadow-sm border border-gray-200">
                        {dictionary?.sellingForm?.statsSourceLabel || 'Source'}: Statbel
                      </span>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {!isLoadingStats && (
          <>
            <p className="text-zinc-800 mt-4">
              {dictionary?.sellingForm?.statsMessage}
            </p>

            {/* --- Living Area & Plot Area Row (Stage 0+) --- */}
            <div className="flex gap-2 mt-2">
              {/* Living Area */}
              {/* Wrap input and error */}
              <div className="flex-1">
                <div className={`relative ${formStyles.inputContainerBaseClasses}`}> {/* Input container */}
                  <input
                    type="number"
                    name="livingArea"
                    inputMode="numeric"
                    placeholder={dictionary?.sellingForm?.livingArea || 'Living Area'}
                    // Apply input classes, add padding for indicator
                    className={`${inputClasses} pr-10`}
                    value={livingArea}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '' : Number(e.target.value);
                      setLivingArea(value);
                      if (value !== '' && !isNaN(Number(value)) && Number(value) > 0 && visibleStage === 0) {
                        if (propertyType === 'apartment' || (propertyType === 'house' && plotArea !== '' && !isNaN(Number(plotArea)) && Number(plotArea) > 0)) {
                          setVisibleStage(1);
                        }
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                    onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }} // Prevent arrow key changes
                  />
                  {/* Static Unit Indicator */}
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                    m²
                  </span>
                </div>
                {/* Error message outside */}
                {errors.livingArea && <p className={formStyles.errorMessageClasses}>{errors.livingArea}</p>}
              </div>

              {/* Plot Area (House Only) */} 
              {propertyType === 'house' && (
                <div className="flex-1">
                  <div className={`relative ${formStyles.inputContainerBaseClasses}`}> {/* Input container */}
                    <input
                      type="number"
                      name="plotArea"
                      inputMode="numeric"
                      placeholder={dictionary?.sellingForm?.plotArea || 'Plot Area'}
                      // Apply input classes, add padding for indicator
                      className={`${inputClasses} pr-10`}
                      value={plotArea}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : Number(e.target.value);
                        setPlotArea(value);
                        if (propertyType === 'house' && value !== '' && !isNaN(Number(value)) && Number(value) > 0 && visibleStage === 0) {
                          if (livingArea !== '' && !isNaN(Number(livingArea)) && Number(livingArea) > 0) {
                            setVisibleStage(1);
                          }
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                      onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }} // Prevent arrow key changes
                    />
                    {/* Static Unit Indicator */}
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                      m²
                    </span>
                  </div>
                  {/* Error message outside */}
                  {errors.plotArea && <p className={formStyles.errorMessageClasses}>{errors.plotArea}</p>}
                </div>
              )}

              {/* Spacer for Living Area width when apartment */}
              {propertyType === 'apartment' && <div className="flex-1"></div>}
            </div>
            {/* --- End Living Area & Plot Area Row --- */}

            {/* --- Bedrooms & Bathrooms Row (Stage 1+) --- */}
            {visibleStage >= 1 && (
              <div className="flex gap-2 mt-2">
                {/* Bedrooms (Always shown if row is visible) */} 
                {/* Wrap input and error */}
                <div className="flex-1">
                  <div className={`relative ${formStyles.inputContainerBaseClasses}`}> {/* Input container */}
                    <input
                      type="number"
                      name="bedrooms"
                      inputMode="numeric"
                      placeholder={dictionary?.sellingForm?.bedrooms || 'Number of Bedrooms'}
                      // Apply input classes, add padding for indicator
                      className={`${inputClasses} pr-10`}
                      value={bedrooms}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : Number(e.target.value);
                        setBedrooms(value);
                        if (value !== '' && !isNaN(Number(value)) && Number(value) >= 0 && visibleStage === 1) { 
                          setVisibleStage(2);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                      onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }} // Prevent arrow key changes
                    />
                    {/* Dynamic Pluralization Indicator */}
                    {bedrooms !== '' && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                        {(typeof bedrooms === 'number' ? bedrooms : Number(bedrooms)) === 1 // Explicit check
                          ? (dictionary?.sellingForm?.bedroomSingular || 'bedroom') 
                          : (dictionary?.sellingForm?.bedroomPlural || 'bedrooms')}
                      </span>
                    )}
                  </div>
                  {/* Error message outside */}
                  {errors.bedrooms && <p className={formStyles.errorMessageClasses}>{errors.bedrooms}</p>}
                </div>

                {/* Bathrooms (Stage 2+) */} 
                {visibleStage >= 2 && (
                  <div className="flex-1">
                    <div className={`relative ${formStyles.inputContainerBaseClasses}`}> {/* Input container */}
                      <input
                        type="number"
                        name="bathrooms"
                        inputMode="numeric"
                        placeholder={dictionary?.sellingForm?.bathrooms || 'Number of Bathrooms'}
                        // Apply input classes, add padding for indicator
                        className={`${inputClasses} pr-10`}
                        value={bathrooms}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : Number(e.target.value);
                          setBathrooms(value);
                          if (value !== '' && !isNaN(Number(value)) && Number(value) >= 0 && visibleStage === 2) {
                            setVisibleStage(3);
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                        onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }} // Prevent arrow key changes
                      />
                      {/* Dynamic Pluralization Indicator */}
                      {bathrooms !== '' && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                          {(typeof bathrooms === 'number' ? bathrooms : Number(bathrooms)) === 1 // Explicit check
                            ? (dictionary?.sellingForm?.bathroomSingular || 'bathroom') 
                            : (dictionary?.sellingForm?.bathroomPlural || 'bathrooms')}
                        </span>
                      )}
                    </div>
                    {/* Error message moved outside the input container div */} 
                    {errors.bathrooms && <p className={formStyles.errorMessageClasses}>{errors.bathrooms}</p>}
                  </div>
                )}

                {/* Spacer for Bedrooms width when Bathrooms is hidden */}
                {visibleStage === 1 && <div className="flex-1"></div>}
              </div>
            )}
            {/* --- End Bedrooms & Bathrooms Row --- */}

            {propertyType === 'house' && (
              <> { /* Plot/Facade only shown for houses from stage 3 */}
                {/* --- Facade Radios (Stage 3+ House only) --- */} 
                {visibleStage >= 3 && (
                  <div className="mt-2">
                    <div className="flex items-center gap-1">
                      {/* Facade Option 2 - Apply AddressForm styling */}
                      <label htmlFor="facadeOption2" className="flex items-center cursor-pointer p-2 border rounded-lg hover:bg-gray-50 transition-colors has-[:checked]:border-zinc-900 bg-white/50 has-[:checked]:bg-white">
                        <input
                          type="radio"
                          id="facadeOption2"
                          name="facadeValue"
                          value={2}
                          checked={facadeValue === 2}
                          onChange={() => {
                            setFacadeValue(2);
                            if (visibleStage === 3) setVisibleStage(4);
                          }}
                          // Remove sr-only, match AddressForm classes
                          className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900"
                        />
                        {/* Add ml-2 and text-zinc-800 */}
                        <span className="ml-2 text-base text-zinc-800"> 
                          {dictionary?.sellingForm?.facadeOption2 || '2 facades'}
                        </span>
                      </label>
                      {/* Facade Option 3 - Apply AddressForm styling */}
                      <label htmlFor="facadeOption3" className="flex items-center cursor-pointer p-2 border rounded-lg hover:bg-gray-50 transition-colors has-[:checked]:border-zinc-900 bg-white/50 has-[:checked]:bg-white">
                        <input
                          type="radio"
                          id="facadeOption3"
                          name="facadeValue"
                          value={3}
                          checked={facadeValue === 3}
                          onChange={() => {
                            setFacadeValue(3);
                            if (visibleStage === 3) setVisibleStage(4);
                          }}
                          // Remove sr-only, match AddressForm classes
                          className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900"
                        />
                        {/* Add ml-2 and text-zinc-800 */}
                        <span className="ml-2 text-base text-zinc-800"> 
                          {dictionary?.sellingForm?.facadeOption3 || '3 facades'}
                        </span>
                      </label>
                      {/* Facade Option 4 - Apply AddressForm styling */}
                      <label htmlFor="facadeOption4" className="flex items-center cursor-pointer p-2 border rounded-lg hover:bg-gray-50 transition-colors has-[:checked]:border-zinc-900 bg-white/50 has-[:checked]:bg-white">
                        <input
                          type="radio"
                          id="facadeOption4"
                          name="facadeValue"
                          value={4}
                          checked={facadeValue === 4}
                          onChange={() => {
                            setFacadeValue(4);
                            if (visibleStage === 3) setVisibleStage(4);
                          }}
                          // Remove sr-only, match AddressForm classes
                          className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900"
                        />
                        {/* Add ml-2 and text-zinc-800 */}
                        <span className="ml-2 text-base text-zinc-800"> 
                          {dictionary?.sellingForm?.facadeOption4 || '4 facades'}
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Submit Button (Stage 3+ for Apt and House) */}
            {((propertyType === 'apartment' && visibleStage >= 3) || (propertyType === 'house' && visibleStage >= 3)) && (
              <button
                type="submit"
                disabled={disableSubmit}
                // Apply button classes using constants
                className={`${formStyles.submitButtonBaseClasses} ${
                  disableSubmit ? formStyles.submitButtonDisabledClasses : formStyles.submitButtonHoverClasses
                }`}
              >
                {dictionary?.sellingForm?.submit || 'Submit'}
              </button>
            )}
          </>
        )}
      </form>

      <style jsx>{`
        .input-container { 
          overflow: hidden;
        };
      `}</style>
    </>
  );
} 